import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { SimpleAdvancedToggle } from './SimpleAdvancedToggle'
import type { FindingsSummary } from '@/utils/findingsSummary'

interface Props {
  personaName: string
  summary: FindingsSummary
  dirty: boolean
  onSave: () => void
  saving: boolean
  avatarUrl?: string | null
  /** Optional so the 8 pre-existing tests that omit it keep passing. */
  onViewPrompt?: () => void
  /** Opens the Version History drawer. Optional for the same reason. */
  onViewHistory?: () => void
  /** Active bundle version, shown on the saved chip ("Saved ✓ · v42"). */
  version?: number
}

function chip(summary: FindingsSummary) {
  if (summary.blocking > 0) {
    return { text: `${summary.blocking} blocking`, cls: 'bg-destructive/10 text-destructive', label: `Persona health: ${summary.blocking} blocking` }
  }
  if (summary.suggestions > 0) {
    return {
      text: `✓ Ready · ${summary.suggestions} suggestion${summary.suggestions === 1 ? '' : 's'}`,
      cls: 'bg-success/10 text-success',
      label: `Persona health: ready, ${summary.suggestions} suggestion${summary.suggestions === 1 ? '' : 's'}`,
    }
  }
  return { text: '✓ Ready', cls: 'bg-success/10 text-success', label: 'Persona health: ready' }
}

export function WorkshopTopBar({ personaName, summary, dirty, onSave, saving, avatarUrl, onViewPrompt, onViewHistory, version }: Props) {
  const c = chip(summary)
  const initial = (personaName || '?').charAt(0).toUpperCase()
  return (
    <div className="flex items-center justify-between gap-4 border-b border-border bg-primary/5 px-4 py-3">
      <div className="flex items-center gap-3">
        <Link to="/personas" className="text-xs font-medium text-primary hover:underline">← Personas</Link>
        {avatarUrl ? (
          <img src={avatarUrl} alt={personaName} className="h-8 w-8 rounded-full object-cover" />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-linear-to-br from-brand-accent to-brand-primary text-sm font-semibold text-white">
            {initial}
          </div>
        )}
        <span className="font-semibold text-foreground">{personaName}</span>
        <span aria-label={c.label} className={`rounded-full px-2.5 py-0.5 text-xs ${c.cls}`}>{c.text}</span>
      </div>
      <div className="flex items-center gap-3">
        {dirty ? (
          <span className="flex items-center gap-1.5 text-xs font-medium text-warning-strong">
            <span aria-hidden className="h-2 w-2 rounded-full bg-warning" />
            Unsaved changes
          </span>
        ) : (
          <span
            className="text-xs text-muted-foreground"
            title="Every save is a version — open History to restore."
          >
            {version != null ? `Saved ✓ · v${version}` : 'Saved ✓'}
          </span>
        )}
        {onViewPrompt ? (
          <Button size="sm" variant="secondary" onClick={onViewPrompt}>
            Composed prompt
          </Button>
        ) : null}
        {onViewHistory ? (
          <Button size="sm" variant="secondary" onClick={onViewHistory}>
            History
          </Button>
        ) : null}
        <SimpleAdvancedToggle />
        <Button
          size="sm"
          onClick={onSave}
          disabled={saving || !dirty}
          className="bg-primary hover:bg-primary/90 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  )
}
